import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import apiClient from '../../services/api';
import FormButton from '../../components/common/stories/FormButton';

/**
 * TokenEntryForm - Enter ADM- (admin license) or INV- (invitation) tokens
 *
 * Token Types:
 * - ADM-xxxx: Admin license token (from super admin after payment)
 *   → Grants admin role + ability to create organization
 * - INV-xxxx: Invitation token (from organization admin)
 *   → Grants agent role + joins existing organization
 */
const TokenEntryForm = ({ onTokenRedeemed }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [token, setToken] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [tokenInfo, setTokenInfo] = useState(null);

  // Validate token format
  const isValidTokenFormat = (value) => {
    return value.startsWith('ADM-') || value.startsWith('INV-');
  };

  const getTokenType = (value) => {
    if (value.startsWith('ADM-')) return 'admin_license';
    if (value.startsWith('INV-')) return 'invitation';
    return null;
  };

  const handleTokenChange = (e) => {
    const value = e.target.value.toUpperCase();
    setToken(value);
    setError('');
    setTokenInfo(null);
  };

  const handleValidate = async () => {
    if (!token.trim()) {
      setError('Please enter a token');
      return;
    }

    if (!isValidTokenFormat(token)) {
      setError('Invalid token format. Token should start with ADM- or INV-');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      // Validate token with backend (public endpoint, no auth needed for validation)
      const response = await apiClient.post('/api/tokens/validate', { token });

      if (response.data.valid) {
        setTokenInfo(response.data);
      } else {
        setError(response.data.error || 'Invalid or expired token');
      }
    } catch (err) {
      console.error('Token validation error:', err);
      setError(err.response?.data?.error || 'Failed to validate token. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleContinue = async () => {
    if (!tokenInfo) return;

    const isAdminToken = tokenInfo.type === 'admin_license';

    if (isAdminToken) {
      // For ADM tokens: don't redeem yet — pass validation data + token string to parent.
      // Redemption happens in OrganizationSetupForm where org name is collected.
      onTokenRedeemed({
        type: tokenInfo.type,
        token: token,
        maxLocations: tokenInfo.max_locations,
      });
    } else {
      // For INV tokens: redeem immediately (no org name needed)
      setIsValidating(true);
      setError('');

      try {
        const authToken = await getAccessTokenSilently();
        const response = await apiClient.post(
          '/api/tokens/redeem',
          { token },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        // API returns { type: "user_invitation", organization: { id, name }, role, ... }
        onTokenRedeemed({
          type: 'invitation',
          tokenId: response.data.tokenId,
          organizationId: response.data.organization?.id || response.data.organization_id,
          organizationName: response.data.organization?.name || response.data.organization_name,
          role: response.data.role,
          locations: response.data.locations,
          maxLocations: response.data.max_locations,
        });
      } catch (err) {
        console.error('Token redemption error:', err);
        setError(err.response?.data?.error || 'Failed to redeem token. Please try again.');
        setTokenInfo(null);
      } finally {
        setIsValidating(false);
      }
    }
  };

  const renderTokenInfo = () => {
    if (!tokenInfo) return null;

    const isAdminToken = tokenInfo.type === 'admin_license';

    return (
      <div className="token-info-card">
        <div className="token-info-header">
          <span className={`token-type-badge ${isAdminToken ? 'admin' : 'invitation'}`}>
            {isAdminToken ? 'Admin License' : 'Invitation'}
          </span>
          <span className="token-status-badge valid">Valid</span>
        </div>

        {isAdminToken ? (
          <div className="token-info-body">
            <p className="token-info-description">
              This token grants you <strong>Administrator</strong> access.
              You will be able to create your organization and set up store locations.
            </p>
            {tokenInfo.max_locations && (
              <p className="token-info-detail">
                <span className="detail-label">Maximum Locations:</span>
                <span className="detail-value">{tokenInfo.max_locations}</span>
              </p>
            )}
          </div>
        ) : (
          <div className="token-info-body">
            <p className="token-info-description">
              You have been invited to join <strong>{tokenInfo.organization_name}</strong> as an <strong>{tokenInfo.role}</strong>.
            </p>
            {tokenInfo.locations && tokenInfo.locations.length > 0 && (
              <div className="token-info-detail">
                <span className="detail-label">Assigned Locations:</span>
                <ul className="location-list">
                  {tokenInfo.locations.map((loc, idx) => (
                    <li key={idx}>{loc.store_name || loc.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="token-info-footer">
          <FormButton className="Button" onClick={handleContinue} disabled={isValidating}>
            {isValidating ? 'Processing...' : isAdminToken ? 'Continue to Organization Setup' : 'Accept Invitation'}
          </FormButton>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .token-form-container {
          width: 100%;
          max-width: 500px;
          margin: 0 auto;
          padding: 0 20px;
          box-sizing: border-box;
        }

        .token-form-description {
          text-align: center;
          margin-bottom: 24px;
          color: #6b7280;
          font-size: 14px;
          line-height: 1.6;
        }

        .token-input-group {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 16px;
        }

        .token-input-wrapper {
          display: flex;
          gap: 12px;
        }

        @media (max-width: 480px) {
          .token-input-wrapper {
            flex-direction: column;
          }
        }

        .token-input {
          flex: 1;
          padding: 14px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          font-size: 16px;
          font-family: 'Monaco', 'Consolas', monospace;
          letter-spacing: 0.5px;
          color: #111827;
          background-color: #fff;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .token-input:focus {
          outline: none;
          border-color: #ff8264;
          box-shadow: 0 0 0 3px rgba(255, 130, 100, 0.15);
        }

        .token-input::placeholder {
          color: #9ca3af;
          font-family: inherit;
        }

        .token-input.has-error {
          border-color: #ef4444;
        }

        .validate-btn {
          padding: 14px 24px;
          background: #ff8264;
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s, transform 0.1s;
          white-space: nowrap;
        }

        .validate-btn:hover:not(:disabled) {
          background: #e5745a;
          transform: translateY(-1px);
        }

        .validate-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .error-message {
          color: #ef4444;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .error-message svg {
          flex-shrink: 0;
        }

        .token-info-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          margin-top: 20px;
        }

        .token-info-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .token-type-badge {
          padding: 4px 12px;
          border-radius: 9999px;
          font-size: 12px;
          font-weight: 600;
        }

        .token-type-badge.admin {
          background: #dbeafe;
          color: #1e40af;
        }

        .token-type-badge.invitation {
          background: #d1fae5;
          color: #065f46;
        }

        .token-status-badge {
          padding: 4px 10px;
          border-radius: 9999px;
          font-size: 11px;
          font-weight: 500;
        }

        .token-status-badge.valid {
          background: #d1fae5;
          color: #065f46;
        }

        .token-info-body {
          margin-bottom: 16px;
        }

        .token-info-description {
          font-size: 14px;
          color: #374151;
          line-height: 1.6;
          margin: 0 0 12px 0;
        }

        .token-info-detail {
          font-size: 13px;
          color: #6b7280;
          margin: 8px 0;
        }

        .detail-label {
          font-weight: 500;
          color: #374151;
          margin-right: 8px;
        }

        .detail-value {
          color: #111827;
        }

        .location-list {
          margin: 8px 0 0 20px;
          padding: 0;
          color: #374151;
        }

        .location-list li {
          margin: 4px 0;
        }

        .token-info-footer {
          border-top: 1px solid #e5e7eb;
          padding-top: 16px;
        }

      `}</style>

      <div className="token-form-container">
        <p className="token-form-description">
          Enter your access code to continue. If you purchased a license, enter the Admin code provided.
          If you received an invitation, enter the invitation code from your email.
        </p>

        <div className="token-input-group">
          <div className="token-input-wrapper">
            <input
              type="text"
              className={`token-input ${error ? 'has-error' : ''}`}
              placeholder="ADM-XXXX-XXXX or INV-XXXX-XXXX"
              value={token}
              onChange={handleTokenChange}
              disabled={isValidating}
              autoComplete="off"
              spellCheck="false"
            />
            <button
              className="validate-btn"
              onClick={handleValidate}
              disabled={isValidating || !token.trim()}
            >
              {isValidating ? 'Checking...' : 'Verify'}
            </button>
          </div>

          {error && (
            <div className="error-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}
        </div>

        {renderTokenInfo()}
      </div>
    </>
  );
};

export default TokenEntryForm;
